import type { DirectoryUserRecord } from '../directory/DirectoryApiService';
import type { HierarchyLevel } from './IdpStructureTypes';

export interface IdpHierarchyStrategy {
    providerFamily: string;
    matches(providerTypeId: string): boolean;
    levelNames: string[];
    levels(user: DirectoryUserRecord): (HierarchyLevel | null)[];
}

function attr(user: DirectoryUserRecord, key: string): string | null {
    const value: unknown = user.attributes?.[key];
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function toLevels(parts: (string | null | undefined)[]): (HierarchyLevel | null)[] {
    return parts.map((p) => {
        const trimmed = typeof p === 'string' ? p.trim() : '';
        return trimmed.length > 0 ? { key: trimmed, label: trimmed } : null;
    });
}

function deslug(segment: string): string {
    return segment.replace(/-and-/g, ' & ').replace(/-/g, ' ');
}

const GOOGLE: IdpHierarchyStrategy = {
    providerFamily: 'google-workspace',
    matches: (id) => id.startsWith('google-workspace/'),
    levelNames: ['Organization', 'Business unit', 'Department', 'Team'],
    levels: (user) => {
        const segments = (user.orgUnitPath ?? '').split('/').map((s) => s.trim()).filter((s) => s.length > 0);
        return GOOGLE.levelNames.map((_, i) => {
            const seg = segments[i];
            return seg !== undefined ? { key: seg, label: deslug(seg) } : null;
        });
    },
};

const OKTA: IdpHierarchyStrategy = {
    providerFamily: 'okta',
    matches: (id) => id.startsWith('okta/'),
    levelNames: ['Organization', 'Business unit', 'Department', 'Team'],
    levels: (user) =>
        toLevels([attr(user, 'organization'), attr(user, 'division'), user.department, attr(user, 'team') ?? attr(user, 'group')]),
};

const ENTRA: IdpHierarchyStrategy = {
    providerFamily: 'entra-id',
    matches: (id) => id.startsWith('entra-id/'),
    levelNames: ['Organization', 'Business unit', 'Department'],
    levels: (user) => toLevels([attr(user, 'companyName'), attr(user, 'division'), user.department]),
};

const FALLBACK: IdpHierarchyStrategy = {
    providerFamily: 'generic',
    matches: () => true,
    levelNames: ['Department'],
    levels: (user) => toLevels([user.department]),
};

const STRATEGIES: IdpHierarchyStrategy[] = [GOOGLE, OKTA, ENTRA];

export function strategyFor(providerTypeId: string): IdpHierarchyStrategy {
    return STRATEGIES.find((s) => s.matches(providerTypeId)) ?? FALLBACK;
}
